import React, { useCallback, memo, useMemo } from 'react';
import { View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusIcon from 'design-system/StatusIcon';
import IconCircle from 'design-system/icons/IconCircle';
import IconTransfer from 'design-system/icons/IconTransfer';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import FormattedAmount from './FormattedAmount';
import webActivityRowStyle from './webActivityRowStyle';
import useStockMeta from '../hooks/useStockMeta';
import { ROW_ITEM_VERTICAL_PADDING, ROW_ITEM_HORIZONTAL_PADDING } from '../constants';
import { isWeb } from '../../../constants';
const SwitchActivityItem = ({ item, isFirstInSection, isLastInSection, withWrapper }) => {
    const fromStockCache = useStockMeta(item.assetId);
    const toStockCache = useStockMeta(item.switchAssetId);
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const onPress = useCallback(() => {
        navigation.navigate('InvestSwitchOrder', {
            id: item.id,
            status: item.status,
            assetId: item.assetId,
            switchOrderId: item.switchOrderId,
            switchAssetId: item.switchAssetId,
        });
    }, [item]);
    const borderStyles = useMemo(() => [isFirstInSection && styles.firstItem, isLastInSection && styles.lastItem], [isFirstInSection, styles.firstItem, styles.lastItem, isLastInSection]);
    const webRowStyle = withWrapper ? webActivityRowStyle : undefined;
    const StatusInfo = useMemo(() => {
        switch (item.status) {
            case 'completed':
                return (<View style={styles.infoRow}>
            <StatusIcon state="done" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 Positive>
              Filled
            </Text>
          </View>);
            case 'partially_filled':
                return (<View style={styles.infoRow}>
            <StatusIcon state="done" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 Positive>
              Partially filled
            </Text>
          </View>);
            case 'queued':
                return (<View style={styles.infoRow}>
            <StatusIcon state="in-progress" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Queued
            </Text>
          </View>);
            case 'pending':
                return (<View style={styles.infoRow}>
            <StatusIcon state="in-progress" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Pending
            </Text>
          </View>);
            case 'cancelled':
                return (<View style={styles.infoRow}>
            <StatusIcon state="cancelled-red" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Cancelled
            </Text>
          </View>);
            case 'failed':
                return (<View style={styles.infoRow}>
            <StatusIcon state="cancelled-red" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Failed
            </Text>
          </View>);
            default:
                return null;
        }
    }, [item.status, styles.infoRow]);
    return (<View style={withWrapper ? [styles.wrapper, borderStyles] : null}>
      <TouchableHighlight hoverOnLight onCard={isWeb} onPress={onPress} activeOpacity={1} style={withWrapper && !isWeb ? borderStyles : null} underlayColor={colors.background.underlay}>
        <View style={webRowStyle ? [styles.itemContainer, webRowStyle] : styles.itemContainer}>
          <IconCircle width={44} height={44}>
            <IconTransfer colorKey="brand"/>
          </IconCircle>
          <Spacer w={16}/>
          <View style={styles.flex}>
            <Text Text-14 Gray>
              SWITCH
            </Text>
            <Text Text-16 numberOfLines={1}>
              From {fromStockCache?.name || item.assetId} to {toStockCache?.name || item.switchAssetId}
            </Text>
            <Spacer h={2}/>
            <View style={styles.flex}>{StatusInfo}</View>
          </View>
          <Spacer w={16}/>
          <FormattedAmount amount={'operation' in item ? item.estimated?.amount : item.amount}/>
        </View>
      </TouchableHighlight>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    itemContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(ROW_ITEM_HORIZONTAL_PADDING),
        paddingVertical: rem(ROW_ITEM_VERTICAL_PADDING),
        // On web the card is padded by 8 and each row by 8, so the hover highlight sits inside the card
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    infoContainer: { flex: 1 },
    infoRow: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flexText: {
        flexShrink: 1,
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && { paddingTop: rem(8) }),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && { paddingBottom: rem(8) }),
    },
    flex: {
        flex: 1,
    },
}));
const PureSwitchActivityItem = memo(SwitchActivityItem);
// PureSwitchActivityItem.whyDidYouRender = true;
export default PureSwitchActivityItem;
